import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { CRow, CCol, Box, Caption, H5 } from "@appcues/component-library";
import ThemeFontDropdown from "components/Themes/ThemeEdit/ThemeEditTab/ThemeFontDropdown";
import ThemeDropDown from "components/Themes/ThemeEdit/ThemeEditTab/ThemeDropdown";
import ThemeColorPicker from "components/Themes/ThemeEdit/ThemeEditTab/ThemeColorPicker";
import ThemeInput from "components/Themes/ThemeEdit/ThemeEditTab/ThemeInput";
import ThemeSlider from "components/Themes/ThemeEdit/ThemeEditTab/ThemeSlider";
import { withSkeleton } from "components/Common/WithSkeleton";
import { getFontNameFromGoogleUrl } from "fonts";

const inputProps = {
  maxWidth: "95%",
  height: "30px",
  marginTop: "16px"
};
const dropdownProps = {
  width: "170px",
  height: "40px",
  marginRight: "24px"
};

const InputField = withSkeleton(ThemeInput, inputProps);
const FontDropDownField = withSkeleton(ThemeFontDropdown, dropdownProps);
const DropDownField = withSkeleton(ThemeDropDown, dropdownProps);
const ColorPickerField = withSkeleton(ThemeColorPicker, dropdownProps);

const ThemeEditSubSection = ({ label, draftTheme, setDraftTheme }) => {
  return label.fields.map(subLabel => {
    return (
      <NonInputFields
        key={`${label.name}-container`}
        label={subLabel}
        draftTheme={draftTheme}
        setDraftTheme={setDraftTheme}
      />
    );
  });
};

export const NonInputFields = ({ label, draftTheme, setDraftTheme }) => {
  return (
    <CRow key={label.name} between="xs" marginTop="16px">
      <StyledCCol>
        <Caption bold type="secondary">
          {label.label}
        </Caption>
      </StyledCCol>
      <CCol>
        {label.type === "fontdropdown" && (
          <FontDropDownField
            value={draftTheme[label.name]}
            defaultOptions={label.options}
            customFontUrls={
              draftTheme.customFontUrl &&
              (Array.isArray(draftTheme.customFontUrl)
                ? draftTheme.customFontUrl
                : [draftTheme.customFontUrl])
            }
            typekitId={draftTheme.typekitId || draftTheme.projectId || ""}
            onChange={value =>
              setDraftTheme(draftTheme => ({
                ...draftTheme,
                [label.name]: value
              }))
            }
            onAddGoogleFont={({ url }) =>
              setDraftTheme(draftTheme => {
                const customFontUrls = new Set(draftTheme.customFontUrl || []);
                customFontUrls.add(url);
                return {
                  ...draftTheme,
                  customFontUrl: Array.from(customFontUrls),
                  [label.name]: getFontNameFromGoogleUrl(url)
                };
              })
            }
            onChangeTypekitId={({ value: typekitId }) =>
              setDraftTheme(draftTheme => ({
                ...draftTheme,
                typekitId,
                projectId: typekitId,
                [label.name]: typekitId
              }))
            }
            onDelete={url =>
              setDraftTheme(draftTheme => ({
                ...draftTheme,
                customFontUrl: draftTheme.customFontUrl.filter(
                  fontUrl => fontUrl !== url
                )
              }))
            }
            // draftTheme is required for withSkeleton :facepalm:
            draftTheme={draftTheme}
          />
        )}
        {label.type === "dropdown" && (
          <DropDownField
            draftTheme={draftTheme}
            setDraftTheme={setDraftTheme}
            label={label}
          />
        )}
        {label.type === "slider-px" && (
          <ThemeSlider
            draftTheme={draftTheme}
            setDraftTheme={setDraftTheme}
            label={label}
            unit="pixel"
            max={20}
          />
        )}
        {label.type === "slider-opacity" && (
          <ThemeSlider
            draftTheme={draftTheme}
            setDraftTheme={setDraftTheme}
            label={label}
            unit="percentage"
            max={100}
          />
        )}
        {label.type.includes("colorpicker") && (
          <ColorPickerField
            draftTheme={draftTheme}
            setDraftTheme={setDraftTheme}
            label={label}
          />
        )}
      </CCol>
    </CRow>
  );
};

export const ThemeEditSection = ({
  contentArray,
  draftTheme,
  setDraftTheme
}) => {
  return (
    <div>
      {contentArray.map(label => {
        if (label.category) {
          return (
            <Box key={label.category}>
              <StyledContainer>
                <H5 type="tertiary">{label.category}</H5>
              </StyledContainer>
              <ThemeEditSubSection
                label={label}
                draftTheme={draftTheme}
                setDraftTheme={setDraftTheme}
              />
            </Box>
          );
        }
        return label.type === "input" ? (
          <InputField
            draftTheme={draftTheme}
            setDraftTheme={setDraftTheme}
            label={label}
            key={label.name}
          />
        ) : (
          <NonInputFields
            key={`${label.name}-container`}
            label={label}
            draftTheme={draftTheme}
            setDraftTheme={setDraftTheme}
          />
        );
      })}
    </div>
  );
};

ThemeEditSection.propTypes = {
  contentArray: PropTypes.array.isRequired,
  draftTheme: PropTypes.object.isRequired,
  setDraftTheme: PropTypes.func.isRequired
};

export default ThemeEditSection;

const StyledCCol = styled(CCol)`
  align-self: center;
`;

const StyledContainer = styled.div`
  margin-top: 40px;
`;
